import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import LoadingButton from "@mui/lab/LoadingButton";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { vestResolver } from "@hookform/resolvers/vest";
import debounce from "lodash/debounce";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import Dialog from "components/dialog";
import ErrorSnackbarContent from "components/error-snackbar-content";
import ContactFields from "features/calls/rolodex/contact-fields.component";
import { CONTACT_DIALOG_PAPER_SX } from "features/calls/rolodex/contact-form-dialog.component";
import {
  ContactFormValues,
  EMPTY_CONTACT_FORM,
  contactValidator
} from "features/calls/rolodex/contact-form.validator";
import {
  AttachCallRequest,
  CallContact,
  DialstackContact,
  NumberBelongsToContactError,
  useGetContactsQuery
} from "services/dialstack";

import { displayNumber } from "../call-display.util";
import type { AttachOutcome } from "./use-unknown-caller-actions.hook";

const CONTACT_SEARCH_LIMIT = 20;

type Mode = "existing" | "new";

type SaveContactDialogProps = {
  remoteNumber: string;
  fromName: string | null;
  onClose: () => void;
  onAttach: (
    request: Omit<AttachCallRequest, "callId">
  ) => Promise<AttachOutcome>;
  // Root styles (the softphone lifts its dialogs over the panel).
  sx?: SxProps;
};

// RTK error shape for the backend's explicit duplicate-number conflict.
export function numberBelongsToContact(error: unknown): CallContact | null {
  const data = (error as { data?: Partial<NumberBelongsToContactError> } | null)
    ?.data;
  return data?.code === "number_belongs_to_contact" && data.contact
    ? data.contact
    : null;
}

export function contactOptionLabel(contact: DialstackContact) {
  return contact.organization
    ? `${contact.name} · ${contact.organization}`
    : contact.name;
}

// Records a non-patient caller (insurer, vendor, a relative's own line) so
// future calls from this number auto-match. Either picks an existing contact,
// which learns the number, or creates one — a single attach request either
// way, so nothing is left half-saved if the response is lost.
export default function SaveContactDialog({
  remoteNumber,
  fromName,
  onClose,
  onAttach,
  sx
}: SaveContactDialogProps) {
  const dispatch = useAppDispatch();
  const [mode, setMode] = useState<Mode>("new");
  const [selected, setSelected] = useState<DialstackContact | null>(null);
  const [search, setSearch] = useState("");
  const [conflict, setConflict] = useState<CallContact | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The same fields as the Rolodex form, minus numbers (the call's number is
  // the contact's) and email.
  const form = useForm<ContactFormValues>({
    mode: "onBlur",
    resolver: vestResolver(contactValidator({ withPhoneNumbers: false })),
    defaultValues: {
      ...EMPTY_CONTACT_FORM,
      name: fromName?.trim() ?? "",
      phone_numbers: []
    }
  });
  const name = useWatch({ control: form.control, name: "name" });

  const debouncedSetSearch = useMemo(() => debounce(setSearch, 250), []);
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const { data: contacts = [], isFetching } = useGetContactsQuery({
    search: search.trim() || undefined,
    limit: CONTACT_SEARCH_LIMIT
  });

  const submit = async (request: Omit<AttachCallRequest, "callId">) => {
    setIsSubmitting(true);
    setConflict(null);
    const error = await onAttach(request);
    setIsSubmitting(false);
    if (!error) {
      onClose();
      return;
    }
    const owner = numberBelongsToContact(error.error);
    if (owner) {
      setConflict(owner);
      return;
    }
    dispatch(setCustomMessage(<ErrorSnackbarContent response={error} />));
    dispatch(setOpen(true));
  };

  const submitNewContact = form.handleSubmit((values) =>
    submit({
      new_contact: {
        name: values.name.trim(),
        organization: values.organization.trim(),
        notes: values.notes.trim(),
        kind: values.kind
      }
    })
  );

  const handleSubmit = () => {
    if (mode === "existing") {
      if (selected) submit({ contact_id: selected.id });
      return;
    }
    submitNewContact();
  };

  const canSubmit =
    mode === "existing" ? selected !== null : (name ?? "").trim().length > 0;

  return (
    <Dialog
      title="Save caller to the Rolodex"
      subtitle={`${displayNumber(remoteNumber)} will be saved to the contact`}
      open
      onClose={onClose}
      // The Rolodex dialogs' 720 px: the shared Dialog ignores "sm" below its
      // 1321 px breakpoint and goes full width, which on a laptop puts the
      // buttons under the softphone panel.
      maxWidth="md"
      paperContentSx={CONTACT_DIALOG_PAPER_SX}
      sx={sx}
      actions={
        <>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disabled={!canSubmit}
            loading={isSubmitting}
            onClick={handleSubmit}
            data-testid="save-contact-submit"
          >
            {mode === "existing" ? "Attach to contact" : "Save contact"}
          </LoadingButton>
        </>
      }
    >
      <Stack spacing={2} sx={{ pb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Future calls from this number will show the contact&apos;s name
          instead of an unknown caller.
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(_, next: Mode | null) => {
            if (next) {
              setMode(next);
              setConflict(null);
            }
          }}
          aria-label="Contact"
        >
          <ToggleButton value="new" data-testid="save-contact-mode-new">
            New contact
          </ToggleButton>
          <ToggleButton
            value="existing"
            data-testid="save-contact-mode-existing"
          >
            Existing contact
          </ToggleButton>
        </ToggleButtonGroup>
        {mode === "existing" ? (
          <Autocomplete
            options={contacts}
            value={selected}
            onChange={(_, value) => setSelected(value)}
            onInputChange={(_, value, reason) => {
              if (reason === "input") debouncedSetSearch(value);
            }}
            // Server-side search: the API already filtered, so never let the
            // Autocomplete re-filter (it would drop organization/number hits).
            filterOptions={(options) => options}
            getOptionLabel={contactOptionLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isFetching}
            noOptionsText="No contacts match"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Contact"
                placeholder="Search by name, organization or number"
                size="small"
                inputProps={{
                  ...params.inputProps,
                  "data-testid": "save-contact-search"
                }}
              />
            )}
          />
        ) : (
          <FormProvider {...form}>
            <Stack
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                submitNewContact();
              }}
              data-testid="save-contact-form"
            >
              <ContactFields variant="attach" />
            </Stack>
          </FormProvider>
        )}
        {conflict && (
          <Stack
            spacing={1}
            data-testid="save-contact-conflict"
            sx={{ p: 1.5, borderRadius: 1, bgcolor: "warning.light" }}
          >
            <Typography variant="body2">
              {displayNumber(remoteNumber)} already belongs to{" "}
              <strong>{conflict.name}</strong>
              {conflict.organization ? ` (${conflict.organization})` : ""}.
            </Typography>
            <LoadingButton
              variant="outlined"
              size="small"
              loading={isSubmitting}
              onClick={() => submit({ contact_id: conflict.id })}
              sx={{ alignSelf: "flex-start" }}
              data-testid="save-contact-attach-owner"
            >
              Attach to {conflict.name} instead
            </LoadingButton>
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
}
